import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import pull from 'lodash.pull';
import debounce from 'lodash.debounce';
import { aggridSearchBarActions } from 'actions/AggridSearchBar';
import { searchActions } from 'actions/Search';
import { catalogActions } from 'actions/Catalog';
import { aggridFilterConstants } from 'constants/AggridFilter.constants';
import Icon from 'components/Icon/Icon';
import { IoMdClose } from '@react-icons/all-files/io/IoMdClose';
import { selectCurrentPlatformId } from 'selectors/platform';
import { catalogService } from 'services';
import { store, getValueOfAttribut } from 'helpers';
import SuggestionList from './SuggestionList';

const SearchBar = ({
  currentPlatformId,
  handleClickFilter,
  currentFilters,
  setCurrentFilters,
  infosLogClient,
  onBlur,
  onClearInput,
  onChange,
  aggridSearchBar,
  fetchTopSellingItems,
}) => {
  const [suggestionListIsVisible, setSuggestionListIsVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [topSellingItems, setTopSellingItems] = useState([]);

  // Ref for the search input (used for Cmd+K / Ctrl+K focus shortcut)
  const inputRef = useRef(null);

  // Use refs to capture latest values and avoid stale closures
  const currentFiltersRef = useRef(currentFilters);
  const setCurrentFiltersRef = useRef(setCurrentFilters);
  const handleClickFilterRef = useRef(handleClickFilter);
  const onChangeRef = useRef(onChange);

  // Update refs when props change
  useEffect(() => {
    currentFiltersRef.current = currentFilters;
    setCurrentFiltersRef.current = setCurrentFilters;
    handleClickFilterRef.current = handleClickFilter;
    onChangeRef.current = onChange;
  }, [currentFilters, setCurrentFilters, handleClickFilter, onChange]);

  useEffect(() => {
    const aggridSearchBarValue = (aggridSearchBar && aggridSearchBar.searchValue) || '';
    setSearchValue(aggridSearchBarValue);
  }, []);

  useEffect(() => {
    fetchTopSellingItems(currentPlatformId).then((response) => {
      if (response && response.topSellingItems) {
        const { topSellingItems } = response;
        setTopSellingItems(topSellingItems);
      }
    });
  }, [fetchTopSellingItems, currentPlatformId]);

  // Create debounced search function - uses refs to access latest values
  const debouncedSearch = useMemo(
    () =>
      debounce((inputValue) => {
        onChangeRef.current(
          inputValue,
          handleClickFilterRef.current,
          currentFiltersRef.current,
          setCurrentFiltersRef.current,
          setSuggestionListIsVisible,
          setSearchValue
        );
      }, 500), // 500ms delay
    [] // Empty dependencies - debounce function is stable, refs provide latest values
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Cmd+K (Mac) / Ctrl+K (Windows-Linux): focus search input and show suggestions
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        if (inputRef.current) {
          inputRef.current.focus();
        }
        setSuggestionListIsVisible(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearchChange = useCallback(
    (inputValue) => {
      // Update UI immediately for responsive feel
      setSearchValue(inputValue);

      // Handle empty search immediately - use refs to get latest values
      if (inputValue.length === 0) {
        debouncedSearch.cancel();
        onChangeRef.current(
          inputValue,
          handleClickFilterRef.current,
          currentFiltersRef.current,
          setCurrentFiltersRef.current,
          setSuggestionListIsVisible,
          setSearchValue
        );
        return;
      }

      // Debounce the actual search
      debouncedSearch(inputValue);
    },
    [debouncedSearch] // Only debouncedSearch dependency - refs provide latest values
  );

  const updateSearchValue = (value) => {
    handleSearchChange(value);
  };

  const onFocusHandler = () => {
    setSuggestionListIsVisible(true);
  };

  return (
    <>
      <span>
        <Icon content={'\\e93e'} />
      </span>
      <div className="aggrid-searchbar__wrapper">
        <input
          ref={inputRef}
          id="inputSearch"
          type="text"
          placeholder="Rechercher un produit"
          onChange={(e) => handleSearchChange(e.target.value)}
          onBlur={(e) => onBlur(e, infosLogClient, setSuggestionListIsVisible)}
          onFocus={() => onFocusHandler()}
          value={searchValue}
          className="search-box"
          autoComplete="off"
        />
        {searchValue && (
          <span>
            <IoMdClose
              fontSize="18px"
              id="clearSearch"
              onClick={() => onClearInput(handleClickFilter, setSearchValue, searchValue)}
            />
          </span>
        )}
      </div>

      <SuggestionList
        isVisible={suggestionListIsVisible}
        items={topSellingItems}
        updateSearchValue={updateSearchValue}
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const {
    aggridSearchBar,
    user,
    platform: { selectedId: platformId },
  } = state;
  const {
    information: { id, type, username },
  } = user;
  const infosLog = {
    id: user && id,
    username,
    type: user && type,
    platformId,
  };

  return {
    aggridSearchBar,
    infosLogClient: infosLog,
    currentPlatformId: selectCurrentPlatformId(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  onChange: (
    inputValue,
    handleClickFilter,
    currentFilters,
    setCurrentFilters,
    setSuggestionListIsVisible,
    setSearchValue
  ) => {
    if (inputValue.length === 0) {
      setSearchValue('');
      handleClickFilter('');
      dispatch(aggridSearchBarActions.setSearchBar(''));
      return;
    }
    setSearchValue(inputValue);
    dispatch(aggridSearchBarActions.searchForProduct(inputValue));
    if (inputValue.length > 2) {
      setSuggestionListIsVisible(false);
      let newActiveFilters = {};
      const newQuickList = pull(
        currentFilters.quickList,
        aggridFilterConstants.FILTER_FAVORITE,
        aggridFilterConstants.FILTER_PROMOTION,
        aggridFilterConstants.FILTER_TOP_SALES
      );
      newActiveFilters = {
        ...currentFilters,
        quickList: newQuickList,
      };
      setCurrentFilters(newActiveFilters);
      handleClickFilter(inputValue);
    }
  },
  onBlur: (event, obj, setSuggestionListIsVisible) => {
    setSuggestionListIsVisible(false);
    const valSearch = event.currentTarget.value;
    const objLog = {
      search_value: valSearch,
      username: obj.username,
      type: obj.type,
      platform: { id: obj.platformId },
    };
    if (valSearch.length > 3) {
      dispatch(searchActions.searchLogs(objLog));
    }
  },
  onClearInput: (handleClickFilter, setSearchValue, searchValue) => {
    setSearchValue('');
    handleClickFilter(searchValue);
    dispatch(aggridSearchBarActions.setSearchBar(searchValue));
  },
  fetchTopSellingItems: (platformId) => dispatch(catalogActions.fetchTopSellingItems(platformId)),
});

SearchBar.propTypes = {
  infosLogClient: PropTypes.object,
  handleClickFilter: PropTypes.func,
  currentFilters: PropTypes.object,
  setCurrentFilters: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchBar);
